import apiClient from './api'

function normalizeError(error, fallbackMessage) {
  const status = Number(error?.response?.status || 0)
  const message = error?.response?.data?.message || error?.message || fallbackMessage
  return { status, message, raw: error }
}

function normalizeListResponse(response) {
  if (Array.isArray(response?.data?.data)) return response.data.data
  if (Array.isArray(response?.data?.notifications)) return response.data.notifications
  if (Array.isArray(response?.data)) return response.data
  return []
}

function normalizeEntityResponse(response) {
  return response?.data?.data ?? response?.data ?? null
}

const notificationService = {
  async listNotifications({ status = 'all', limit = 50, cursor } = {}) {
    try {
      const params = { status, limit }
      if (cursor) params.cursor = cursor
      const response = await apiClient.get('/notifications', { params })
      return normalizeListResponse(response)
    } catch (error) {
      throw normalizeError(error, 'Unable to load notifications')
    }
  },

  async getUnreadCount() {
    try {
      const response = await apiClient.get('/notifications/unread-count')
      return Number(response?.data?.unread || 0)
    } catch (error) {
      throw normalizeError(error, 'Unable to load unread count')
    }
  },

  async markNotificationRead(id) {
    try {
      const response = await apiClient.patch(`/notifications/${encodeURIComponent(id)}/read`)
      return normalizeEntityResponse(response)
    } catch (error) {
      throw normalizeError(error, 'Unable to mark notification as read')
    }
  },

  async markAllNotificationsRead() {
    try {
      const response = await apiClient.patch('/notifications/read-all')
      return Number(response?.data?.count || 0)
    } catch (error) {
      throw normalizeError(error, 'Unable to mark all notifications as read')
    }
  },

  async archiveNotification(id) {
    try {
      const response = await apiClient.patch(`/notifications/${encodeURIComponent(id)}/archive`)
      return normalizeEntityResponse(response)
    } catch (error) {
      throw normalizeError(error, 'Unable to archive notification')
    }
  },

  async deleteNotification(id) {
    const encodedId = encodeURIComponent(id)
    try {
      await apiClient.delete(`/notifications/${encodedId}`)
      return { id }
    } catch (error) {
      throw normalizeError(error, 'Unable to delete notification')
    }
  },

  async listBackgroundOperations(filters = {}) {
    try {
      const response = await apiClient.get('/admin/background-operations', { params: filters })
      return normalizeListResponse(response)
    } catch (error) {
      throw normalizeError(error, 'Unable to load background operations')
    }
  },

  async getBackgroundOperation(id) {
    try {
      const response = await apiClient.get(`/admin/background-operations/${encodeURIComponent(id)}`)
      return normalizeEntityResponse(response)
    } catch (error) {
      throw normalizeError(error, 'Unable to load background operation')
    }
  },

  async cancelBackgroundOperation(id) {
    try {
      const response = await apiClient.post(`/admin/background-operations/${encodeURIComponent(id)}/cancel`)
      return normalizeEntityResponse(response)
    } catch (error) {
      throw normalizeError(error, 'Unable to cancel background operation')
    }
  },
}

export default notificationService
